import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SendResult } from '../../repositories/SimpleRepository';
import { orderBy } from 'lodash';

export interface UseUserRepositoryResult {
    updateUser: (request: UpdateUserRequest) => Promise<void>;
    createPlay: (request: CreatePlayRequest) => Promise<CreatePlayResponse | undefined>;
    deletePlay: (request: DeletePlayRequest) => Promise<void>;
    listPlays: (request: ListPlaysRequest) => Promise<ListPlaysResponseItem[] | undefined>;
    listUsers: (request: ListUsersRequest) => Promise<ListUsersResponseItem[] | undefined>;
}

export interface UpdateUserRequest {
    userId: string;
    userSecret: string;
    name: string;
    icon: string;
}

export interface CreatePlayRequest {
    userId: string;
    userSecret: string;

    gameId: string;
    playedOn: string;
}

export interface CreatePlayResponse {
    userId: string;
    playId: string;
    gameId: string;
    playedOn: string;
}

export interface DeletePlayRequest {
    userId: string;
    userSecret: string;

    playId: string;    
}

export interface ListPlaysRequest {
    userId: string;
    userSecret: string;
}

export interface ListPlaysResponseItem {
    userId: string;
    playId: string;
    gameId: string;
    playedOn: string;
}

export interface ListUsersRequest {
    userId: string;
    userSecret: string;
}

export interface ListUsersResponseItem {
  userId: string;
  name: string;
  icon: string;
}

export const useUserRepository = (): UseUserRepositoryResult => {
    const queryClient = useQueryClient();
    const CACHE_TTL_MS = 60_000;

    const sendAnon = useCallback(async (method: string, path: string, headers: { userId: string; userSecret: string }, body?: string): Promise<SendResult> => {
        try {
            const response = await fetch('https://2wns5ds9z2.execute-api.us-east-1.amazonaws.com/prod' + path, {
                method: method,
                mode: 'cors',
                headers: {
                    Accept: 'application/json',
                    'x-anon-auth': btoa(`${headers.userId}|${headers.userSecret}`)
                },
                body: body
            });
            return { ok: response.ok, body: await response.text() };
        } catch (e) {
            console.error(e);
            return { ok: false, body: JSON.stringify(e) };
        }
    }, []);

    const updatePlaysCache = useCallback((updater: (current: ListPlaysResponseItem[] | undefined) => ListPlaysResponseItem[] | undefined) => {
        queryClient.setQueriesData<ListPlaysResponseItem[] | undefined>({ queryKey: ['userRepository', 'plays'] }, updater);
    }, [queryClient]);

    const updateUsersCache = useCallback((updater: (current: ListUsersResponseItem[] | undefined) => ListUsersResponseItem[] | undefined) => {
        queryClient.setQueriesData<ListUsersResponseItem[] | undefined>({ queryKey: ['userRepository', 'users'] }, updater);
    }, [queryClient]);

    const updateUserMutation = useMutation<void, unknown, UpdateUserRequest>({
        mutationFn: async (request) => {
            console.log(`Updating user: ${JSON.stringify(request)}`);
            const response = await sendAnon(
                'PUT',
                `/anon-api/play-tracker/users/${request.userId}`,
                { userId: request.userId, userSecret: request.userSecret },
                JSON.stringify({ name: request.name, icon: request.icon })
            );
            if (!response.ok) {
                console.warn(response.body);
            }
        },
        onSuccess: (_data, request) => {
            updateUsersCache((current) => {
                if (!current) {
                    return current;
                }

                return current.map((user) => user.userId === request.userId ? { ...user, name: request.name, icon: request.icon } : user);
            });
        },
    });

    const createPlayMutation = useMutation<CreatePlayResponse | undefined, unknown, CreatePlayRequest>({
        mutationFn: async (request) => {
            console.log(`Creating play: ${JSON.stringify(request)}`);
            const response = await sendAnon(
                'POST',
                `/anon-api/play-tracker/plays`,
                { userId: request.userId, userSecret: request.userSecret },
                JSON.stringify({ gameId: request.gameId, playedOn: request.playedOn })
            );
            if (!response.ok) {
                console.warn(response.body);
                return undefined;
            }
            return JSON.parse(response.body) as CreatePlayResponse;
        },
        onSuccess: (_data, request) => {
            if (!_data) {
                return;
            }

            updatePlaysCache((current) => {
                const nextPlay: ListPlaysResponseItem = {
                    userId: _data.userId,
                    playId: _data.playId,
                    gameId: _data.gameId,
                    playedOn: _data.playedOn,
                };

                const nextPlays = [nextPlay, ...(current ?? [])];
                return orderBy(nextPlays, 'playedOn', 'desc');
            });
        },
    });

    const deletePlayMutation = useMutation<void, unknown, DeletePlayRequest>({
        mutationFn: async (request) => {
            console.log(`Deleting play: ${JSON.stringify(request)}`);
            const response = await sendAnon(
                'DELETE',
                `/anon-api/play-tracker/plays/${request.playId}`,
                { userId: request.userId, userSecret: request.userSecret }
            );
            if (!response.ok) {
                console.warn(response.body);
            }
        },
        onSuccess: (_data, request) => {
            updatePlaysCache((current) => {
                if (!current) {
                    return current;
                }

                return current.filter((play) => play.playId !== request.playId);
            });
        },
    });

    const listPlays = useCallback(
        async (request: ListPlaysRequest): Promise<ListPlaysResponseItem[] | undefined> => {
            const queryKey = ['userRepository', 'plays', request.userId, request.userSecret];
            return queryClient.fetchQuery({
                queryKey,
                queryFn: async () => {
                    console.log(`Listing plays: ${JSON.stringify(request)}`);
                    const response = await sendAnon(
                        'GET',
                        `/anon-api/play-tracker/plays`,
                        { userId: request.userId, userSecret: request.userSecret }
                    );
                    if (!response.ok) {
                        console.warn(response.body);
                        return undefined;
                    }
                    return orderBy((JSON.parse(response.body) as ListPlaysResponseItem[]), 'playedOn', 'desc');
                },
                staleTime: CACHE_TTL_MS,
                gcTime: CACHE_TTL_MS,
            });
        },
        [queryClient, sendAnon, CACHE_TTL_MS]
    );

    const listUsers = useCallback(
        async (request: ListUsersRequest): Promise<ListUsersResponseItem[] | undefined> => {
            const queryKey = ['userRepository', 'users', request.userId, request.userSecret];
            return queryClient.fetchQuery({
                queryKey,
                queryFn: async () => {
                    console.log(`Listing users: ${JSON.stringify(request)}`);
                    const response = await sendAnon(
                        'GET',
                        `/anon-api/play-tracker/users`,
                        { userId: request.userId, userSecret: request.userSecret }
                    );
                    if (!response.ok) {
                        console.warn(response.body);
                        return undefined;
                    }
                    return JSON.parse(response.body) as ListUsersResponseItem[];
                },
                staleTime: CACHE_TTL_MS,
                gcTime: CACHE_TTL_MS,
            });
        },
        [queryClient, sendAnon, CACHE_TTL_MS]
    );

    return useMemo(
        () => ({
            updateUser: (request: UpdateUserRequest) => updateUserMutation.mutateAsync(request),
            createPlay: (request: CreatePlayRequest) => createPlayMutation.mutateAsync(request),
            deletePlay: (request: DeletePlayRequest) => deletePlayMutation.mutateAsync(request),
            listPlays,
            listUsers,
        }),
        [createPlayMutation, deletePlayMutation, listPlays, listUsers, updateUserMutation]
    );
};
